import React, { useState, useEffect, useCallback } from "react";
import classNames from "classnames";
import { Helmet } from "react-helmet";
import { unstable_useMediaQuery as useMediaQuery } from "@material-ui/core/useMediaQuery";
import { withStyles, withTheme } from "@material-ui/core/styles";
import { withRouter } from "react-router-dom";
import { formatApiDateTime } from "../../tools/formatApiDateTime";
import queryString from "query-string";
import createStyles from "@material-ui/core/styles/createStyles";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import TableBody from "../common/TableBody";
import EnhancedTableHeader from "../common/EnhancedTableHeader";
import withContext from "../../tools/withContext";
import UserActionButton from "./components/UserActionButton";
import UsersActionButton from "./components/UsersActionButton";
import TableNavigation from "../common/TableNavigation";
import UiSearchInputField from "../common/UiSearchInputField";
import FilteringProjectDialog from "./components/FilteringProjectDialog";
import UserFormDialog from "./components/UserFormDialog";
import ContextType from "../../types/Context";
import UserType from "../../types/User";
import ProjectType from "../../types/Project";
import useRouter from "../../tools/useRouter";
import LocalDimensionsContainer from "./components/LocalDimensionsContainer";
import { tablePageStyles, tablePageRootStyles, tablePageToolbarStyles } from "../common/tableTheme";

export const mappingUserRoles: any = {
  ROLE_ADMIN: "Admin",
  ROLE_LABELER: "Labeler",
  ROLE_PROJECT_ADMIN: "Project Admin",
};

const styles = (theme: Theme) =>
  createStyles({
    root: {
      ...tablePageRootStyles(theme),
    },
    ...tablePageStyles(theme),
    ...tablePageToolbarStyles(theme),
    noData: {
      textAlign: "center",
      padding: theme.spacing.unit * 12,
      color: theme.palette.text.secondary,
    },
    fullWidth: {
      width: "100%",
    },
  });

interface PropsType {
  classes: any;
  ctx: ContextType;
  theme: any;
  saveUser: Function;
  deleteUser: Function;
  resetToken2FA: Function;
  users: UserType[];
  isLoading: boolean;
  projects: ProjectType[];
  pagination: any;
  location: any;
  history: any;
  match: any;
}

const Users = (props: PropsType) => {
  const {
    classes,
    users,
    projects,
    saveUser,
    deleteUser,
    resetToken2FA,
    isLoading,
    pagination,
    match: {
      params: { noPage },
    },
    theme,
  } = props;

  const router = useRouter();
  const searchParsed = router.query;
  const s = searchParsed.searchTerm || "";

  const [order, setOrder] = useState(
    //@ts-ignore
    searchParsed.sort ? searchParsed.sort.split(",")[1] : "desc"
  );

  const [orderBy, setOrderBy] = useState(
    //@ts-ignore
    searchParsed.sort ? searchParsed.sort.split(",")[0] : "firstName"
  );

  //@ts-ignore
  const [searchTerm, setSearchTerm] = useState<string>(s);

  const [projectsSelected, setProjectSelected] = useState<any[]>([]);

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [userSelected, setUserSelected] = useState<any>(null);

  const matches = useMediaQuery(theme.breakpoints.down("lg"));

  const findUrlProjectsSelected = useCallback(() => {
    const urlP =
      typeof searchParsed.projectIds === "string"
        ? searchParsed.projectIds.split(",")
        : searchParsed.projectIds;
    if (!urlP) return undefined;
    return urlP
      .map((pId: string) => projects.find((p: ProjectType) => p.id === pId))
      .filter((p: any) => !!p)
      .map((p: any) => p.id);
  }, [searchParsed, projects]);

  const handleRequestSort = (event: any, property: string) => {
    let _orderBy = property;
    let _order = "desc";

    if (orderBy === property && order === "desc") {
      _order = "asc";
    }

    props.history.push(
      `/userlist/${noPage}?${queryString.stringify(
        { ...searchParsed, sort: `${_orderBy},${_order}` },
        { strict: false }
      )}`
    );
    setOrderBy(_orderBy);
    setOrder(_order);
  };

  const handleChangePage = (noP: any) => {
    props.history.push(
      `/userlist/${noP}?${queryString.stringify(router.query)}`
    );
  };

  const handleSearch = (str: string) => {
    const trimmed = str.trim();
    setSearchTerm(trimmed);
    props.history.push(
      `/userlist/${trimmed.length > 0 ? "0" : noPage}?${queryString.stringify({
        ...router.query,
        searchTerm: trimmed.length > 0 ? trimmed : undefined,
      })}`
    );
  };

  useEffect(() => {
    if (projects.length > 0) {
      const pr = findUrlProjectsSelected();

      if (pr) {
        setProjectSelected(pr);
      }
    }
  }, [projects]);

  const buildColumns = (usr: UserType) => [
    <div style={{ textTransform: "capitalize", paddingLeft: "10px", paddingRight: "10px" }}>{usr.firstName}</div>,
    <div style={{ textTransform: "capitalize", paddingLeft: "10px", paddingRight: "10px" }}>{usr.lastName}</div>,
    <div>{formatApiDateTime(usr.createdDate)}</div>,
    <div>{usr.email}</div>,
    <div>{mappingUserRoles[usr.authorities[0]]}</div>,
    <div style={{ paddingLeft: "10px", paddingRight: "10px" }}>
      {usr.projects
        .map((id: any) => {
          const proj = projects.find((pro: any) => pro.id === id);
          if (!proj) return "";
          return `${proj.name} (${proj.client.name})`;
        })
        .filter((p: any) => (p.length > 1 && p[0] === "" ? p[0] : p))
        .join(", ")}
    </div>,
    // <div>{usr.createdDateInt}</Grid>,
    <div>
      <UserActionButton
        user={usr}
        onButtonClicked={() => setUserSelected(usr)}
        saveUser={saveUser}
        deleteUser={deleteUser}
        resetToken2FA={resetToken2FA}
        projects={projects}
        openDialog={openDialog}
        closeDialog={closeDialog}
        variant="modern"
      />
    </div>,
  ];

  // const usersOrderedFiltered = stableSort(users, getSorting(order, orderBy));
  const usersOrderedFiltered = users;

  const onProjectClicked = (projectId: string) =>
    setProjectSelected(
      projectsSelected.includes(projectId)
        ? projectsSelected.filter((pId: string) => pId !== projectId)
        : projectsSelected.concat([projectId])
    );

  const clearFilter = () => {
    setProjectSelected([]);
    props.history.push(
      `/userlist/${noPage}?${queryString.stringify({
        ...router.query,
        projectIds: undefined,
      })}`
    );
  };

  const onFilteringSubmitted = () => {
    const projectIds =
      projectsSelected.length > 0 ? projectsSelected.join(",") : undefined;
    props.history.push(
      `/userlist/${noPage}?${queryString.stringify({
        ...router.query,
        projectIds,
      })}`
    );
  };

  const handleSelection = (e: any, id: string) => {
    setUserSelected(users.find((u: any) => u.id === id));
  };

  const closeDialog = () => setIsDialogOpen(false);

  const openDialog = () => setIsDialogOpen(true);

  const handleSave = (u: UserType) => {
    saveUser(u).then(() => closeDialog());
  };

  const columns = [
    {
      id: "firstName",
      numeric: false,
      disablePadding: true,
      label: "Prénom",
      onlyRoles: null,
      size: 1.5,
      align: "left",
      sortable: true,
    },
    {
      id: "lastName",
      numeric: false,
      disablePadding: false,
      label: "Nom",
      onlyRoles: null,
      size: 1.5,
      align: "left",
      sortable: true,
    },
    {
      id: "createdDate",
      numeric: false,
      sortable: true,
      disablePadding: false,
      label: "Date de création",
      onlyRoles: null,
      size: 2,
      align: "center",
    },
    {
      id: "email",
      numeric: true,
      disablePadding: false,
      label: "Email",
      onlyRoles: null,
      size: 2,
      align: "center",
      sortable: true,
    },
    {
      id: "role",
      numeric: false,
      disablePadding: false,
      label: "Rôle",
      onlyRoles: null,
      size: 1.5,
      align: "center",
      sortable: true,
    },
    {
      id: "projects",
      numeric: false,
      disablePadding: false,
      label: "Projets",
      onlyRoles: null,
      filter: true,
      filterActive: !!searchParsed.projectIds,
      filterComponent: ({ open, onClose }: { open: boolean; onClose: any }) => (
        <FilteringProjectDialog
          projects={projects}
          projectsSelected={projectsSelected}
          open={open}
          onClose={onClose}
          onClick={onProjectClicked}
          clearFilter={clearFilter}
          onFilteringSubmitted={onFilteringSubmitted}
          isAlreadyFiltred={!!searchParsed.projectIds}
        />
      ),
      size: 2,
      align: "center",
      sortable: false,
    },
    {
      id: "actions",
      numeric: false,
      sortable: false,
      disablePadding: false,
      label: "Actions",
      onlyRoles: null,
      size: 1,
      align: "right",
    },
  ];

  return (
    <Grid container direction="column" className={classes.root}>
      <Helmet>
        <title>Liste des utilisateurs</title>
      </Helmet>
      <UserFormDialog
        open={isDialogOpen}
        onClose={closeDialog}
        onSave={handleSave}
        projects={projects}
        user={userSelected}
      />
      <Grid item className={classes.fullWidth}>
        <div className={classes.pageToolbar}>
          <div className={classes.pageToolbarLeft}>
            <div className={classes.pageTitleRow}>
              <Typography
                component="h2"
                className={classNames(classes.pageTitle, {
                  [classes.pageTitleCompact]: matches,
                })}
              >
                Liste des utilisateurs
              </Typography>
            </div>
          </div>
          <div className={classes.pageToolbarRight}>
            <div className={classes.pageLoaderSlot}>
              {isLoading && (
                <CircularProgress
                  color="secondary"
                  size={20}
                  className={classes.pageLoader}
                />
              )}
            </div>
            <UsersActionButton
              toolbarVariant="modern"
              saveUser={saveUser}
              projects={projects}
            />
            <div className={classes.searchArea}>
              <div className={classes.searchGroup}>
                <div className={classes.searchFieldInGroupOnly}>
                  <UiSearchInputField
                    searchString={searchTerm}
                    setSearchString={handleSearch}
                    variant="modern"
                    inGroup
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Grid>
      <Grid item className={classes.tableWrapper}>
        <Paper elevation={0} className={classes.tableCard}>
          <EnhancedTableHeader
            variant="modern"
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={users.length}
            columns={columns}
            withCheckBox={false}
          />
          {usersOrderedFiltered.length === 0 && !isLoading && (
            <Typography className={classes.tableEmptyInCard} variant="h6">
              Aucun utilisateur
              {searchTerm !== "" && " ne correspond a votre recherche"}
            </Typography>
          )}
          {usersOrderedFiltered.length > 0 && (
            <div className={classes.tableScrollArea}>
              <LocalDimensionsContainer variant="modern">
                <TableBody
                  variant="modern"
                  handleSelection={handleSelection}
                  buildColumns={buildColumns}
                  selected={[]}
                  datas={usersOrderedFiltered}
                  columns={columns}
                  noPage={noPage}
                />
              </LocalDimensionsContainer>
            </div>
          )}
          {!isLoading && pagination && pagination.totalElements > 0 && (
            <TableNavigation
              variant="modern"
              pagination={pagination}
              onChangePage={handleChangePage}
              itemName="Utilisateurs"
            />
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default withStyles(styles)(withRouter(withContext(withTheme()(Users))));
